import React from 'react';
import QuadrantChart from 'components/pure/charts/QuadrantChart';
import UserAvatar from 'components/pure/utils/UserAvatar';
import SparkLineTooltipContent from './tooltips/SparkLineTooltipContent';
import ConversationStyleTooltipContent from './tooltips/ConversationStyleTooltipContent';
import RepProfileTrendChart from '$pages/@common/rep-profile-trend-chart/RepProfileTrendChart';
import HelpHover from 'components/pure/utils/HelpHover';
import round from 'utils/round';
import {formatDuration} from 'utils/periodAsText';
import {scalePow} from 'd3-scale';
import './RepProfileSummary.styl';
import withAdHocPlanDialog from 'components/coaching/withAdHocPlanDialog';
import formatDate from 'utils/formatDate';
import RepUpcomingAgenda from 'components/dashboard/RepUpcomingAgenda';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import Loading from 'components/pure/utils/Loading';
import Button from '@material-ui/core/Button';
import {List} from 'immutable';
import KpiProgressBanner from '$pages/$rep-profile/kpi/banner';
import {featureFlags} from 'utils/const';
import hasFlag from 'utils/hasFlag';

export const chartHeight = 300;

export function yScale(quadrantData, chartHeight = 300) {
  if (!quadrantData) {
    return null;
  }
  const max = Math.max(...quadrantData.map(d => d.y), 26);
  return scalePow().rangeRound([chartHeight, 1]).domain([0, max]).exponent(0.34);
}

class RepProfileSummary extends React.PureComponent {
  getQuadrantData(rep, users, interchanges, talkListen, interchangesLoading, talkListenLoading) {
    if (
      interchangesLoading ||
      talkListenLoading ||
      interchanges.isEmpty() ||
      talkListen.isEmpty()
    ) {
      return undefined;
    }

    return (
      users
        .map(u => {
          const tl = talkListen.get(u.id) * 100;
          const i = interchanges.get(u.id);

          return {
            id: u.id,
            selected: rep && u.id === rep.id,
            x: tl ? tl : 0,
            y: i ? i : 0,
            label: (
              <ConversationStyleTooltipContent
                user={u}
                listenPercentage={tl}
                interchangesPerMinuteAvg={i}
                onClick={() => this.props.onSelectRep(u.id)}
              />
            ),
          };
        })
        // filter out users w/o any data
        .filter(s => s.x > 0 && s.y > 0)
    );
  }

  componentWillUnmount() {
    RepProfilePageActions.resetRepData();
  }

  render() {
    const {
      summary,
      rep,
      teammates,
      interchanges,
      talkListen,
      interchangesLoading,
      talkListenLoading,
      headerStartDate,
      headerEndDate,
      onCoachNow,
      isCoach,
      hasNoCallReviewType,
    } = this.props;

    const quadrantData = this.getQuadrantData(
      rep,
      teammates,
      interchanges,
      talkListen,
      interchangesLoading,
      talkListenLoading
    );
    const quadrantLoading = rep && (interchangesLoading || talkListenLoading);

    const periodTextUnitForSingle = formatDuration(headerStartDate, headerEndDate, {
      shouldPluralize: false,
      showUnitForSingles: true,
    });
    const periodText = formatDuration(headerStartDate, headerEndDate, {
      shouldPluralize: true,
    });
    const endDateStr = formatDate(headerEndDate);
    const basedOnText =
      endDateStr === 'Yesterday' || endDateStr === 'Today'
        ? `Last ${periodText}`
        : `Between ${formatDate(headerStartDate)} and ${endDateStr}`;
    const showCoachNow = hasNoCallReviewType && isCoach && rep && !rep.hidden;
    return (
      <div className="rep-profile-summary">
        <section className="main-section">
          <section className="card">
            <div className="card-row">
              <UserAvatar user={rep} className="profile-image" />
              <div className="intro">
                <div className="name">{rep && rep.getFullName()}</div>
              </div>
            </div>
            <div className="card-row">
              <div className="average-call-score">
                <HelpHover
                  className="self"
                  text={() =>
                    `The average scorecard score received by the rep in the ${periodTextUnitForSingle} period of the report.`
                  }>
                  {basedOnText} Average Call Score:
                </HelpHover>
                {summary && (
                  <div>
                    <div className="score">{round(summary.callScoreAvg)}%</div>
                    <div className="team">Team Average: {round(summary.teamScoreAvg)}%</div>
                  </div>
                )}
              </div>
              <div className="three-month-trend">
                {summary && (
                  <RepProfileTrendChart
                    data={summary.callScoreAvgTrend}
                    width={120}
                    height={70}
                    fill={'#2079E4'}
                    tooltipContent={d =>
                      rep &&
                      d && (
                        <div className="tooltip-content">
                          <SparkLineTooltipContent
                            date={d.date}
                            title={`${rep.firstName}'s Average Call Score`}
                            value={d.value + '%'}
                          />
                        </div>
                      )
                    }
                  />
                )}
              </div>
            </div>
            {showCoachNow && (
              <div className="coach-now">
                <Button
                  variant="contained"
                  color="primary"
                  className="ev-button"
                  onClick={() => onCoachNow({rep})}>
                  Coach Now
                </Button>
              </div>
            )}
          </section>
          {quadrantLoading ? (
            <div className={'quadrant-loading'}>
              <Loading />
            </div>
          ) : !quadrantData ? (
            <div className="quadrant-empty-state">No call data in range.</div>
          ) : (
            <QuadrantChart
              data={quadrantData || List()}
              xDomain={[0, 100]}
              width={300}
              height={chartHeight}
              xNegativeLabel="Average Interchanges Per Minute"
              yNegativeLabel="Average Listen Percentage"
              yScale={yScale(quadrantData)}
            />
          )}
        </section>
        {hasFlag(featureFlags.adminKpis) && rep && (
          <div className="kpi-banner-wrapper">
            <KpiProgressBanner dashOnly targetUser={rep} />
          </div>
        )}
        <RepUpcomingAgenda rep={rep} />
      </div>
    );
  }
}

export default withAdHocPlanDialog(RepProfileSummary);
